/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap, ScrollTrigger, Power4 } from 'gsap/all';
import { debounce } from 'lodash';
import Headroom from 'headroom.js';
import Vue from 'vue';
import { SplitText } from './gsap-bonus/SplitText';

import Router from './router';

import store from './store';

import MemberServices from './classes/gms/MemberServices';
import GeneralServices from './classes/gms/GeneralServices';
import ReservationServices from './classes/gms/ReservationServices';
import AcademyReservationServices from './classes/gms/AcademyReservationServices';
import AccountServices from './classes/gms/AccountServices';

gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');

require('./components/action-modal');
require('./components/booking-today-modal');
require('./components/date-time-component');
require('./components/booking-component');
require('./components/booking-overview-component');
require('./components/cookie-bar-component');
require('./components/golfset-modal-component');
require('./components/booker-information-component');
require('./components/course-slider-component');
require('./components/datepicker-component');
require('./components/academy-datepicker-component');
require('./components/dropdown-component');
require('./components/event-form-component');
require('./components/fancy-slider-component');
require('./components/slider-component');
require('./components/ticker-component');
require('./components/two-column-component');
require('./components/member-autocomplete-component');
require('./components/newsletter-signup-component');
require('./components/status-widget-component');
require('./components/contact-form-component');

require('./classic/member/activities-list');
require('./classic/member/profile-overview');
require('./classic/member/edit-profile');
require('./classic/member/change-password');
require('./classic/member/forgot-password');
require('./classic/member/reset-password');
require('./classic/member/terms-conditions');
require('./classic/member/academy-lesson-component');

require('./filters');

Vue.prototype.$api = {
    gms: {
        member: MemberServices,
        reservation: ReservationServices,
        general: GeneralServices,
        academy: AcademyReservationServices,
        account: AccountServices,
    },
};

Vue.prototype.$modals = new Vue();

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],
    store,

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            // router: null,
            // isMobile: false,
            // isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            darkHeader: document.querySelector('#page-container').dataset.darkHeader,
            showFooter: document.querySelector('#page-container').dataset.showFooter,
            header: null,
            activePopUp: null,
            splits: [],
            headroom: null,
            footerHolder: null,
            footerItems: null,
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation(visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
            if (!visibile) {
                this.headroom.unfreeze();
                gsap.set(document.body, {
                    overflow: null,
                });
                return;
            }

            this.headroom.unpin();
            this.headroom.freeze();
            gsap.set(document.body, {
                overflow: 'hidden',
            });
        },
    },

    mounted() {
        // Prepare router.
        this.router = new Router({
            instance: this,
            prefetch: true,
            section: this.$el.dataset.initialSection,
            ignoreComponents: [
                'footer-component',
                'navigation-component',
                'language-selector',
            ],
        });

        window.addEventListener('resize', this.onWindowResize);

        this.header = document.querySelector('.navigation__container');
        this.headroom = new Headroom(this.header);
        this.headroom.init();

        this.buildPage();
        this.footerAnimation();

        EventBus.$on('page-transition-enter-complete', () => {
            this.buildPage();
            this.footerAnimation();
        });
    },

    methods: {
        initScroll() {
            const html = document.documentElement;
            const { body } = document;

            const scroller = {
                target: document.querySelector('#app'),
                ease: 0.035, // <= scroll speed
                endY: 0,
                y: 0,
                resizeRequest: 1,
                scrollRequest: 0,
            };

            let requestId = null;

            gsap.set(scroller.target, {
                rotation: 0.01,
                force3D: true,
            });

            function updateScroller() {
                const resized = scroller.resizeRequest > 0;

                if (resized) {
                    const height = scroller.target.clientHeight;
                    body.style.height = `${height}px`;
                    scroller.resizeRequest = 0;
                }

                const scrollY = window.pageYOffset || html.scrollTop || body.scrollTop || 0;

                scroller.endY = scrollY;
                scroller.y += (scrollY - scroller.y) * scroller.ease;

                if (Math.abs(scrollY - scroller.y) < 0.05 || resized) {
                    scroller.y = scrollY;
                    scroller.scrollRequest = 0;
                }

                gsap.set(scroller.target, {
                    y: -scroller.y,
                });

                requestId = scroller.scrollRequest > 0 ? requestAnimationFrame(updateScroller) : null;
            }

            function onScroll() {
                // eslint-disable-next-line no-plusplus
                scroller.scrollRequest++;
                if (!requestId) {
                    requestId = requestAnimationFrame(updateScroller);
                }
            }

            function onResize() {
                // eslint-disable-next-line no-plusplus
                scroller.resizeRequest++;
                if (!requestId) {
                    requestId = requestAnimationFrame(updateScroller);
                }
            }

            // eslint-disable-next-line no-use-before-define
            window.addEventListener('load', onLoad);

            function onLoad() {
                updateScroller();
                window.focus();
                window.addEventListener('resize', onResize);
                document.addEventListener('scroll', onScroll);
            }

            // ScrollTrigger.scrollerProxy("body", {
            //     scrollTop(value) {
            //       if (arguments.length) {
            //             = value;
            //       }

            //       return bodyScrollBar.scrollTop;
            //     }
            // });
        },

        buildPage() {
            this.$once('hero-title-revealed', () => {
                this.titleReveals();
                this.initRevealTriggers();
            });

            this.heroTitleReveal();
        },

        heroTitleReveal() {
            const elements = [...document.querySelectorAll('.split-title-1')];
            const titleHolder = [...document.querySelectorAll('.hero-title-holder')][0];

            if (elements.length < 1) {
                this.$emit('hero-title-revealed');
                return;
            }

            for (let index = 0; index < elements.length; index++) {
                const element = elements[index];

                gsap.set(element, { 'box-sizing': 'border-box' });
                gsap.set(titleHolder.querySelector('.hero-title__intro'), { opacity: 0 });
                element.dataset.scrollIndex = index;
                const splitLines = new SplitText(element, { type: 'lines,words', linesClass: 'line' });
                const { words, lines } = splitLines;

                gsap.set(words, { opacity: 0, y: '100%', transformOrigin: 'bottom center' });
                gsap.set(element, { autoAlpha: 1 });

                this.splits.push(splitLines);

                for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
                    const line = lines[lineIndex];
                    gsap.to(line.children, {
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 88%',
                            once: true,
                        },
                        y: '0%',
                        duration: 1,
                        stagger: 0.06,
                        ease: Power4.easeOut,
                        opacity: 1,
                        onComplete: () => {
                            gsap.delayedCall(0.4, () => {
                                this.$emit('hero-title-revealed');
                            });

                            gsap.to(titleHolder.querySelector('.hero-title__intro'), { duration: 2, opacity: 1, ease: Power4.easeOut });
                            // splitLines.revert();
                        },
                    });
                }
            }
        },

        onNavigationBeforeEnter(el) {
            gsap.set(el, { clip: `rect(0px, 0px, ${window.innerHeight}px, 0px)` });
            gsap.set(el.querySelector('.navigation-holder__background img'), {
                scale: 2,
                opacity: 0,
                x: '-25%',
            });

            gsap.set(el.querySelectorAll('.navigation__main-holder .nav-item a'), { y: '100%' });
            gsap.set(el.querySelectorAll('.nav-secondary-item a'), { y: '100%' });
            gsap.set(el.querySelector('.navigation__socials-list'), { y: '100%', opacity: 0 });
            gsap.set(el.querySelector('.navigation__members-holder-inner'), { y: '-100%', opacity: 0 });
        },

        onNavigationEnter(el, done) {
            const mainNavItems = [...el.querySelectorAll('.navigation__main-holder .nav-item a')];
            const subNavItems = [...el.querySelectorAll('.nav-secondary-item a'),
            ];

            // Hamburger stuff
            const hamburgerText = [document.querySelector('.header__menu-hamburger-text--open'), document.querySelector('.header__menu-hamburger-text--close')];
            const hamburgerTop = document.querySelector('.header__menu-hamburger--top');
            const hamburgerBottom = document.querySelector('.header__menu-hamburger--bottom');

            gsap.to(hamburgerText, {
                duration: 0.7,
                y: window.innerWidth >= 1440 ? -13 : -11,
                ease: 'power4.out',
            });

            gsap.to(hamburgerTop, {
                duration: 1,
                rotate: 45,
                y: 3,
                x: 4,
                ease: 'power4.out',
            });

            gsap.to(hamburgerBottom, {
                duration: 1,
                rotate: -45,
                y: -12,
                scaleX: 1,
                ease: 'power4.out',
            });

            gsap.to(el, {
                duration: 2,
                clip: `rect(0px, ${window.innerWidth}px, ${window.innerHeight}px, 0px)`,
                ease: Power4.easeOut,
                onComplete: done,
            });

            gsap.to(el.querySelector('.navigation-holder__background img'), {
                duration: 1.6,
                delay: 0.1,
                scale: 1,
                x: 0,
                opacity: 1,
                ease: Power4.easeOut,
            });

            gsap.to(mainNavItems, {
                duration: 2,
                ease: Power4.easeOut,
                stagger: 0.1,
                y: '0%',
                delay: 0.2,
            });

            gsap.to(subNavItems, {
                duration: 2,
                ease: Power4.easeOut,
                stagger: 0.1,
                y: '0%',
                delay: 0.8,
            });

            gsap.to(el.querySelector('.navigation__socials-list'), {
                y: '0%',
                opacity: 1,
                delay: 1.2,
                duration: 0.8,
                ease: Power4.easeOut,
            });

            gsap.to(el.querySelector('.navigation__members-holder-inner'), {
                y: '0%',
                opacity: 1,
                delay: 1,
                duration: 0.8,
                ease: Power4.easeOut,
            });

            gsap.delayedCall(0.05, () => {
                el.querySelector('.navigation__members-holder ').classList.add('--line-reveal');
            });
        },

        onNavigationBeforeLeave(el) {
            gsap.set(el, {
                clip: `rect(0px, ${window.innerWidth}px, ${window.innerHeight}px, 0px)`,
            });
        },

        onNavigationLeave(el, done) {
            const mainNavItems = [...el.querySelectorAll('.navigation__main-holder .nav-item a')];
            const subNavItems = [...el.querySelectorAll('.nav-secondary-item a'),
            ];

            // Hamburger stuff
            const hamburgerText = [document.querySelector('.header__menu-hamburger-text--open'), document.querySelector('.header__menu-hamburger-text--close')];
            const hamburgerTop = document.querySelector('.header__menu-hamburger--top');
            const hamburgerBottom = document.querySelector('.header__menu-hamburger--bottom');

            const speedMultiplier = 0.7;

            gsap.to(hamburgerText, {
                duration: 0.7 * speedMultiplier,
                y: 0,
                ease: 'power4.out',
            });

            gsap.to(hamburgerTop, {
                duration: 1 * speedMultiplier,
                rotate: 0,
                y: 0,
                x: 0,
                ease: 'power4.out',
            });

            gsap.to(hamburgerBottom, {
                duration: 1 * speedMultiplier,
                rotate: 0,
                y: 0,
                scaleX: 0.72,
                ease: 'power4.out',
            });

            gsap.to(el, {
                duration: 2 * speedMultiplier,
                delay: 0.6,
                clip: `rect(0px, 0px, ${window.innerHeight}px, 0px)`,
                ease: Power4.easeOut,
                onComplete: done,
            });

            gsap.to(el.querySelector('.navigation-holder__background img'), {
                duration: 1.6 * speedMultiplier,
                delay: 0.5,
                scale: 2,
                x: '-25%',
                opacity: 0,
                ease: Power4.easeOut,
            });

            gsap.to(mainNavItems, {
                duration: 2 * speedMultiplier,
                ease: Power4.easeOut,
                stagger: 0.1 * speedMultiplier,
                y: '100%',
                delay: 0.6 * speedMultiplier,
            });

            gsap.to(subNavItems, {
                duration: 2 * speedMultiplier,
                ease: Power4.easeOut,
                stagger: 0.1 * speedMultiplier,
                y: '100%',
                delay: 0.2 * speedMultiplier,
            });

            gsap.to(el.querySelector('.navigation__socials-list'), {
                y: '100%',
                opacity: 0,
                delay: 0.2,
                duration: 0.8 * speedMultiplier,
                ease: Power4.easeOut,
            });

            gsap.to(el.querySelector('.navigation__members-holder-inner'), {
                y: '-100%',
                opacity: 0,
                delay: 0.2 * speedMultiplier,
                duration: 0.8 * speedMultiplier,
                ease: Power4.easeOut,
                onComplete: () => {
                    this.headroom.pin();
                },
            });

            gsap.delayedCall(0.05, () => {
                el.querySelector('.navigation__members-holder ').classList.remove('--line-reveal');
            });
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('.a-fade-up'),
                ...document.querySelectorAll('.a-fade-in'),
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
                ...document.querySelectorAll('.square-button__link'),
            ];

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                if (revealPosition) {
                    // console.log(revealPosition);
                }

                const startPercentage = revealPosition || '88%';

                gsap.timeline({
                    scrollTrigger: {
                        trigger: element,
                        start: `top ${startPercentage}`,
                        onEnter: () => {
                            if (!revealDelay) {
                                element.classList.add('--revealed');
                                return;
                            }

                            gsap.delayedCall(revealDelay, () => {
                                element.classList.add('--revealed');
                            });
                        },
                    },
                });
            });
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        titleReveals() {
            const elements = [...document.querySelectorAll('.split-title')];

            for (let index = 0; index < elements.length; index++) {
                const element = elements[index];

                gsap.set(element, { 'box-sizing': 'border-box' });
                element.dataset.scrollIndex = index;
                const splitLines = new SplitText(element, { type: 'lines,words', linesClass: 'line' });
                const { words, lines } = splitLines;

                gsap.set(words, { opacity: 0, y: '100%', transformOrigin: 'bottom center' });
                gsap.set(element, { autoAlpha: 1 });

                this.splits.push(splitLines);

                let delay = 0;
                const delayDuration = 1;
                for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
                    const line = lines[lineIndex];
                    gsap.to(line.children, {
                        scrollTrigger: {
                            trigger: line,
                            start: 'top 88%',
                            once: true,
                        },
                        y: '0%',
                        duration: 1,
                        stagger: 0.06,
                        ease: Power4.easeOut,
                        opacity: 1,
                        delay,
                        onComplete: () => {
                            // splitLines.revert();
                        },
                    });

                    delay += delayDuration;
                }
            }
        },

        footerAnimation() {
            // this.footerHolder = [document.querySelector('.footer__container'), document.querySelector('.footer__mailing-holder')];
            this.footerHolder = document.querySelector('.footer-holder');
            this.footerItems = document.querySelectorAll(['.footer__logo-holder svg', '.nav-item--footer', '.nav-secondary-item--footer', '.footer__locale-holder', '.footer__mailing-signup-holder', '.footer__mailing-title-holder', '.footer__mailing-text-holder', '.navigation__socials-list li', '.footer__social-holder']);
            this.footerMailITems = document.querySelectorAll(['.footer__mailing-signup-holder', '.footer__mailing-title-holder', '.footer__mailing-text-holder', '.navigation__socials-list li', '.footer__social-holder']);

            if (!this.footerHolder) {
                return;
            }

            gsap.set(this.footerItems, {
                y: '100%',
                autoAlpha: 0,
            });

            // for (let i = 0; i < this.footerHolder.length; i++) {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: this.footerHolder,
                    // markers: true,
                    start: 'top 70%',
                    onEnter: () => {
                        this.footerHolder.classList.add('--revealed');
                    },
                },
            });
            tl
            .to(this.footerItems, {
                duration: 1.5,
                ease: Power4.easeOut,
                stagger: 0.2,
                y: '0%',
                autoAlpha: 1,
            }, 0.4)
            .to(this.footerMailITems, {
                duration: 1.5,
                ease: Power4.easeOut,
                stagger: 0.2,
                y: '0%',
                autoAlpha: 1,
            }, '<');
            // }
        },

        onCtaMouseEnter(e) {
            const ctaText = e.target.querySelector('.title-text-cta__cta-text');
            const ctaIconHolder = e.target.querySelector('.title-text-cta__cta-icon-holder');
            const ctaborder = e.target.querySelector('.title-text-cta__cta-border');
            const ctaArrow = ctaIconHolder.querySelector('svg');
            const ctaArrowBody = ctaArrow.querySelector('[data-arrow-body]');
            const ctaArrowHead = ctaArrow.querySelector('[data-arrow-head]');

            const direction = ctaIconHolder.dataset.direction.split('-')[1] || 'right';

            gsap.to(ctaText, {
                duration: 0.8,
                x: -8,
                ease: 'elastic.out(1, 0.4)',
            });

            gsap.to(ctaText, {
                duration: 0.4,
                color: '#BE9D76',
                ease: 'ease',
            });

            gsap.to(ctaborder, {
                duration: 0.8,
                scale: 1.15,
                ease: 'elastic.out(1, 0.4)',
                // ease: 'power4.easeOut'
            });

            switch (direction) {
            case 'up':
                gsap.to(ctaArrowBody, {
                    duration: 0.2,
                    scaleY: 1.57,
                    y: -3,
                    ease: 'ease',
                });

                gsap.to(ctaArrowHead, {
                    duration: 0.2,
                    y: -4,
                    ease: 'ease',
                });
                break;
            case 'left':
                gsap.to(ctaArrowBody, {
                    duration: 0.2,
                    scaleX: 1.57,
                    x: -3,
                    ease: 'ease',
                });

                gsap.to(ctaArrowHead, {
                    duration: 0.2,
                    x: -4,
                    ease: 'ease',
                });
                break;
            case 'down':
                gsap.to(ctaArrowBody, {
                    duration: 0.2,
                    scaleY: 1.57,
                    y: -3,
                    ease: 'ease',
                });

                gsap.to(ctaArrowHead, {
                    duration: 0.2,
                    y: 4,
                    ease: 'ease',
                });
                break;
            default:
                gsap.to(ctaArrowBody, {
                    duration: 0.2,
                    scaleX: 1.57,
                    x: -3,
                    ease: 'ease',
                });

                gsap.to(ctaArrowHead, {
                    duration: 0.2,
                    x: 4,
                    ease: 'ease',
                });
            }
        },

        onCtaMouseLeave(e) {
            const ctaText = e.target.querySelector('.title-text-cta__cta-text');
            const ctaIconHolder = e.target.querySelector('.title-text-cta__cta-icon-holder');
            const ctaborder = e.target.querySelector('.title-text-cta__cta-border');
            const ctaArrow = ctaIconHolder.querySelector('svg');
            const ctaArrowBody = ctaArrow.querySelector('[data-arrow-body]');
            const ctaArrowHead = ctaArrow.querySelector('[data-arrow-head]');

            const direction = ctaIconHolder.dataset.direction.split('-')[1] || 'right';

            gsap.to(ctaText, {
                duration: 0.8,
                x: 0,
                ease: 'elastic.out(1, 0.4)',
            });

            gsap.to(ctaText, {
                duration: 0.4,
                color: '#4A433D',
                ease: 'ease',
            });

            gsap.to(ctaborder, {
                duration: 0.8,
                scale: 1,
                ease: 'elastic.out(1, 0.4)',
            });

            if (direction === 'down' || direction === 'up') {
                gsap.to(ctaArrowBody, {
                    duration: 0.2,
                    scaleY: 1,
                    y: 0,
                    ease: 'ease',
                });

                gsap.to(ctaArrowHead, {
                    duration: 0.2,
                    y: 0,
                    ease: 'ease',
                });
                return;
            }

            gsap.to(ctaArrowBody, {
                duration: 0.2,
                scaleX: 1,
                x: 0,
                ease: 'ease',
            });

            gsap.to(ctaArrowHead, {
                duration: 0.2,
                x: 0,
                ease: 'ease',
            });
        },

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
            // console.log(`Is Mobile: ${this.isMobile}`);
        },

        openPopUp(id) {
            this.activePopUp = id;
        },

        ignoreRouter(event) {
            const el = event.currentTarget;

            window.location = el.href;
        },

        switchLanguage(url, siteName) {
            // Move to URL
            document.location.href = url;
        },

        bookMouseEnter(e) {
            gsap.set(e.currentTarget.querySelector('span'), {
                transformOrigin: 'right center',
            });

            const tl = gsap.timeline();
            tl.to(e.currentTarget.querySelector('span'), {
                scaleX: 0,
                ease: 'power4.easeOut',
                duration: 0.5,
            })
            .to(e.currentTarget.querySelector('span'), {
                transformOrigin: 'left center',
                scaleX: 1,
                ease: 'power4.easeOut',
                duration: 0.5,
            });

            if (e.currentTarget.parentNode.previousElementSibling) {
                tl.to(e.currentTarget.parentNode.previousElementSibling.querySelector('img'), {
                    scale: 1.3,
                    ease: 'power4.easeOut',
                    duration: 1,
                    opacity: 0.75,
                }, 0);
            }
        },

        bookMouseLeave(e) {
            const tl = gsap.timeline();
            if (e.currentTarget.parentNode.previousElementSibling) {
                tl.to(e.currentTarget.parentNode.previousElementSibling.querySelector('img'), {
                    scale: 1,
                    ease: 'power4.easeOut',
                    duration: 1,
                    opacity: 0.5,
                }, 0);
            }
        },

        navigationMouseEnter(e) {
            gsap.set(e.currentTarget.querySelector('span'), {
                transformOrigin: 'right center',
            });

            const tl = gsap.timeline();
            tl.to(e.currentTarget.querySelector('span'), {
                transformOrigin: 'left center',
                scaleX: 1,
                ease: 'power4.easeOut',
                duration: 0.5,
            });

            if (e.currentTarget.parentNode.previousElementSibling) {
                tl.to(e.currentTarget.parentNode.previousElementSibling.querySelector('img'), {
                    scale: 1.3,
                    ease: 'power4.easeOut',
                    duration: 1,
                    opacity: 0.75,
                }, 0);
            }
        },

        navigationMouseLeave(e) {
            const tl = gsap.timeline();
            tl.to(e.currentTarget.querySelector('span'), {
                transformOrigin: 'right center',
                scaleX: 0,
                ease: 'power4.easeOut',
                duration: 0.5,
            });
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
